/**
 * VoicePlayer — compact inline audio player for chat bubbles.
 *
 * Renders a play/pause button + progress bar + duration timer.
 * Uses a single <audio> element per instance; loads metadata lazily so a
 * long chat thread with many voice notes doesn't pre-fetch every clip.
 */
import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

export default function VoicePlayer({ url, durationSec, testid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(durationSec || 0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime || 0);
    const onLoaded = () => { if (a.duration && Number.isFinite(a.duration)) setDur(a.duration); };
    const onEnd = () => { setPlaying(false); setCur(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
  const mmss = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[180px]" data-testid={testid || "voice-player"}>
      <button
        onClick={toggle}
        className="h-9 w-9 rounded-full grid place-items-center bg-primary text-primary-foreground hover:brightness-110 shrink-0"
        title={playing ? "إيقاف" : "تشغيل"}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 rtl:-scale-x-100" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[10px] text-foreground/60 mt-1 tabular-nums">
          {mmss(playing || cur ? cur : dur)}
        </div>
      </div>
      <audio ref={audioRef} src={url} preload="metadata" />
    </div>
  );
}
